/**
 * Validates a file based on type and size constraints
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {{valid: boolean, error: string|null}}
 */
export const validateFile = (file, maxSize) => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (file.type !== 'application/pdf') {
    return { valid: false, error: 'Only PDF files are allowed' };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds ${formatFileSize(maxSize)} limit`
    };
  }

  return { valid: true, error: null };
};

/**
 * Formats bytes into human-readable file size
 * @param {number} bytes - Size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Generates a filename for downloads with suffix
 * @param {string} original - Original filename
 * @param {string} suffix - Suffix to add (e.g., "signed", "split")
 * @returns {string} New filename
 */
export const generateFilename = (original, suffix) => {
  const name = original.replace(/\.pdf$/i, '');
  return `${name}_${suffix}.pdf`;
};

/**
 * Converts a File to ArrayBuffer
 * @param {File} file - File to convert
 * @returns {Promise<ArrayBuffer>}
 */
export const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Triggers a download in the browser
 * @param {Blob} blob - The blob to download
 * @param {string} filename - Filename for the download
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Reads file as data URL for preview
 * @param {File} file - File to read
 * @returns {Promise<string>} Data URL
 */
export const fileToDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
